import React, { useEffect, useState } from 'react';
import { Button, FormGroup, Input, Label, Spinner } from 'reactstrap';
import axios from '../../utils/axiosConfig';
import Swal from 'sweetalert2';
import { formattedGlobalDate } from '../../utils/formattedDates';
import '../../Pages/Css/correccionFechas.css';

const EMPTY_DATES = {
    installationDate: '',
    installationTime: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
};

const getValue = (source, keys) => keys.map((key) => source?.[key]).find((value) => value !== undefined && value !== null && value !== '');

const splitDateTime = (value) => {
    if (!value) return { date: '', time: '' };
    const normalized = String(value).trim().replace('T', ' ');
    const sqlMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})[ ](\d{2}:\d{2}(?::\d{2})?)/);
    if (sqlMatch) return { date: `${sqlMatch[1]}-${sqlMatch[2]}-${sqlMatch[3]}`, time: sqlMatch[4] };

    const latinMatch = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})[ ,]+(\d{2}:\d{2}(?::\d{2})?)/);
    if (latinMatch) return { date: `${latinMatch[3]}-${latinMatch[2]}-${latinMatch[1]}`, time: latinMatch[4] };

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return { date: '', time: '' };
    const pad = (number) => String(number).padStart(2, '0');
    return {
        date: `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`,
        time: `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`,
    };
};

const formatDateTimeForApi = (date, time) => {
    const normalizedTime = time.length === 5 ? `${time}:00` : time;
    return formattedGlobalDate(`${date} ${normalizedTime}`);
};

const ModalCorreccionFechas = ({ isOpen, onClose, onSizeChange }) => {
    const [contract, setContract] = useState('');
    const [contractInfo, setContractInfo] = useState(null);
    const [dates, setDates] = useState(EMPTY_DATES);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setContract('');
            setContractInfo(null);
            setDates(EMPTY_DATES);
        }
    }, [isOpen]);

    useEffect(() => {
        onSizeChange?.(contractInfo ? 'lg' : 'sm');
    }, [contractInfo, onSizeChange]);

    const closeModal = () => {
        setContract('');
        setContractInfo(null);
        setDates(EMPTY_DATES);
        onClose();
    };

    const loadContract = async (event) => {
        event.preventDefault();
        const normalizedContract = contract.trim().toUpperCase();
        if (!normalizedContract) {
            return Swal.fire('Información', 'Ingrese el contrato que desea consultar.', 'info');
        }

        setLoading(true);
        setContractInfo(null);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_SERVER}/contratos/getinfocontrato`,
                { contrato: normalizedContract }
            );
            if (response.data?.success === false) throw new Error(response.data?.message || response.data?.mensaje || 'No fue posible consultar el contrato.');

            const info = Array.isArray(response.data?.data) ? response.data.data[0] : response.data?.data;
            if (!info) throw new Error('No se encontró información para el contrato ingresado.');
            if(info.Active === true) throw new Error('El contrato ingresado se encuentra activo en trafico, Recuerda que solo puedes corregir fechas de contratos históricos.');

            console.log('Información del contrato cargada:', info);

            const installation = splitDateTime(info.InicioServicio);
            const start = splitDateTime(info.FechaHoraInicio);
            const end = splitDateTime(info.FechaHoraFin);
            setContract(normalizedContract);
            setContractInfo(info);
            setDates({
                installationDate: installation.date,
                installationTime: installation.time,
                startDate: start.date,
                startTime: start.time,
                endDate: end.date,
                endTime: end.time,
            });
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || error.response?.data?.mensaje || error.message || 'Error consultando el contrato.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const saveDates = async (event) => {
        event.preventDefault();
        if (Object.values(dates).some((value) => !value)) {
            return Swal.fire('Información', 'Complete todas las fechas y horas.', 'info');
        }

        setSaving(true);
        try {
            const payload = {
                contrato: getValue(contractInfo, ['ContractID', 'ContratoID']) || contract,
                InicioServicio: formatDateTimeForApi(dates.installationDate, dates.installationTime),
                inicio: formatDateTimeForApi(dates.startDate, dates.startTime),
                fin: formatDateTimeForApi(dates.endDate, dates.endTime),
            };

            console.log('Payload para actualizar fechas:', payload);

            const response = await axios.post(
                `${process.env.REACT_APP_SERVER}/contratos/actualizarfechas`,
                payload
            );
            if (response.data?.success === false) throw new Error(response.data?.message || response.data?.mensaje || 'No fue posible actualizar las fechas.');
            await Swal.fire('¡Éxito!', 'Las fechas del contrato se actualizaron correctamente.', 'success');
            closeModal();
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || error.response?.data?.mensaje || error.message || 'Error actualizando las fechas.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const updateDate = (key) => (event) => setDates((current) => ({ ...current, [key]: event.target.value }));

    const dateRow = (label, dateKey, timeKey) => (
        <div className="date-correction-row">
            <Label>{label}</Label>
            <Input aria-label={`${label} fecha`} type="date" value={dates[dateKey]} onChange={updateDate(dateKey)} />
            <Input aria-label={`${label} hora`} type="time" step="1" value={dates[timeKey]} onChange={updateDate(timeKey)} />
        </div>
    );

    return !contractInfo ? (
        <form className="date-correction-search" onSubmit={loadContract}>
            <FormGroup>
                <Label for="date-correction-contract">Contrato</Label>
                <Input
                    id="date-correction-contract"
                    value={contract}
                    onChange={(event) => setContract(event.target.value.toUpperCase())}
                    placeholder="SERV-123456789"
                    autoComplete="off"
                    autoFocus
                />
            </FormGroup>
            <Button type="submit" className="platform-primary-button" disabled={loading}>
                {loading ? <><Spinner size="sm" /> Cargando...</> : 'Cargar información'}
            </Button>
        </form>
    ) : (
        <form className="date-correction-form" onSubmit={saveDates}>
            <div className="date-correction-summary">
                <div><span>Contrato</span><strong>{getValue(contractInfo, ['ContractID', 'ContratoID']) || contract}</strong></div>
                <div><span>Dispositivo</span><strong>{getValue(contractInfo, ['FKLokDeviceID', 'DeviceID']) || 'Sin información'}</strong></div>
            </div>
            <div className="date-correction-fields">
                {dateRow('Fecha/Hora Instalación', 'installationDate', 'installationTime')}
                {dateRow('Fecha/Hora Inicio', 'startDate', 'startTime')}
                {dateRow('Fecha/Hora Fin', 'endDate', 'endTime')}
            </div>
            <div className="date-correction-actions">
                <Button type="button" color="light" onClick={() => setContractInfo(null)} disabled={saving}>Cambiar contrato</Button>
                <Button type="submit" className="platform-primary-button" disabled={saving}>
                    {saving ? <><Spinner size="sm" /> Guardando...</> : 'Grabar'}
                </Button>
            </div>
        </form>
    );
};

export default ModalCorreccionFechas;
